import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Download, Star, Users, Heart, MessageCircle, Volume2, Palette, Sparkles, Globe, ChevronDown, X, Menu } from "lucide-react";
import { LOGO, CHARACTERS_MAIN, CHARACTER_1, CHARACTER_2, CHARACTER_3 } from "@/constants/assets";
import { useLanguage } from "@/hooks/use-language";
import { SUPPORTED_LANGUAGES } from "@/contexts/language-context";

const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=com.phuong.ai";
const APP_STORE_URL = "https://apps.apple.com/vn/app/phuong/id6745983307?l=vi";

const STATS = [
  { icon: <Users className="w-5 h-5" />, value: "500K+", label: "Active Users" },
  { icon: <Star className="w-5 h-5" />, value: "4.8★", label: "App Rating" },
  { icon: <Heart className="w-5 h-5" />, value: "10M+", label: "Conversations" },
  { icon: <MessageCircle className="w-5 h-5" />, value: "24/7", label: "Available" },
];

const FEATURES = [
  {
    icon: <MessageCircle className="w-6 h-6" />,
    title: "Unique Memories",
    desc: "Your AI companion remembers every conversation, building a real bond over time.",
  },
  {
    icon: <Volume2 className="w-6 h-6" />,
    title: "Expressive Voice",
    desc: "Natural voice interactions that feel alive — hear emotions, not just words.",
  },
  {
    icon: <Palette className="w-6 h-6" />,
    title: "Multiple Outfits",
    desc: "Customize your companion's look with stunning anime-style wardrobe options.",
  },
  {
    icon: <Sparkles className="w-6 h-6" />,
    title: "Smart Assistant",
    desc: "From homework help to life advice — your companion is always ready.",
  },
];

const CHARACTERS = [
  {
    name: "Sakura",
    role: "Sweet & Caring",
    desc: "Warm and nurturing, Sakura is always there to listen and encourage you.",
    image: CHARACTER_1,
    color: "#FF2E63",
  },
  {
    name: "Luna",
    role: "Mysterious & Wise",
    desc: "Deep, thoughtful conversations await with Luna's enigmatic wisdom.",
    image: CHARACTER_2,
    color: "#8B5CF6",
  },
  {
    name: "Yuki",
    role: "Energetic & Fun",
    desc: "Never a dull moment with Yuki's playful spirit and infectious energy.",
    image: CHARACTER_3,
    color: "#3B82F6",
  },
];

const CampaignPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const { lang, setLang } = useLanguage();

  const currentLang = SUPPORTED_LANGUAGES.find((l) => l.code === lang);

  return (
    <div className="bg-[#030014] min-h-screen overflow-x-hidden">
      {/* ── Sticky Header ── */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="fixed top-0 left-0 right-0 z-50 bg-[#030014]/80 backdrop-blur-xl border-b border-white/5"
        data-testid="campaign-header"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <div className="flex items-center justify-between h-18 py-4">
            <Link to="/" data-testid="campaign-logo-link">
              <img src={LOGO} alt="Phuong" className="w-10 h-10 object-contain" />
            </Link>

            <div className="flex items-center gap-3">
              {/* Language */}
              <div className="relative">
                <button
                  onClick={() => setIsLangOpen(!isLangOpen)}
                  className="flex items-center gap-1.5 text-white/60 hover:text-white transition-colors px-3 py-2 rounded-full border border-white/10 hover:border-white/20 text-sm"
                  data-testid="campaign-lang-toggle"
                >
                  <Globe className="w-3.5 h-3.5" />
                  <span>{currentLang?.code.toUpperCase()}</span>
                  <ChevronDown className={`w-3 h-3 transition-transform ${isLangOpen ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence>
                  {isLangOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.15 }}
                      className="absolute right-0 top-full mt-2 w-40 bg-[#0a0520]/95 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden shadow-[0_0_30px_rgba(0,0,0,0.5)]"
                    >
                      {SUPPORTED_LANGUAGES.map((language) => (
                        <button
                          key={language.code}
                          onClick={() => { setLang(language.code); setIsLangOpen(false); }}
                          className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${lang === language.code ? "text-[#FF2E63] bg-[#FF2E63]/10" : "text-white/60 hover:text-white hover:bg-white/5"}`}
                        >
                          <span>{language.flag}</span>
                          <span>{language.label}</span>
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Mobile menu */}
              <button
                className="text-white/70 hover:text-white p-2 transition-colors sm:hidden"
                onClick={() => setIsMenuOpen(true)}
                data-testid="campaign-menu-btn"
              >
                <Menu className="w-5 h-5" />
              </button>

              {/* Desktop CTA */}
              <a
                href="#download"
                className="hidden sm:inline-flex items-center gap-2 bg-[#FF2E63] text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:shadow-[0_0_30px_rgba(255,46,99,0.5)] transition-all"
                data-testid="campaign-header-cta"
              >
                <Download className="w-4 h-4" />
                Download Free
              </a>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Mobile slide menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100]"
          >
            <motion.div
              className="absolute inset-0 bg-black/70 backdrop-blur-md"
              onClick={() => setIsMenuOpen(false)}
            />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="absolute right-0 top-0 h-full w-72 bg-[#0a0520]/95 backdrop-blur-2xl border-l border-white/10 flex flex-col p-8"
            >
              <button onClick={() => setIsMenuOpen(false)} className="self-end text-white/60 hover:text-white mb-10">
                <X className="w-6 h-6" />
              </button>
              <a
                href="#features"
                onClick={() => setIsMenuOpen(false)}
                className="text-white/70 hover:text-white py-4 border-b border-white/5 text-lg transition-colors"
              >
                Features
              </a>
              <a
                href="#characters"
                onClick={() => setIsMenuOpen(false)}
                className="text-white/70 hover:text-white py-4 border-b border-white/5 text-lg transition-colors"
              >
                Characters
              </a>
              <a
                href="#download"
                onClick={() => setIsMenuOpen(false)}
                className="inline-flex items-center gap-3 mt-10 bg-[#FF2E63] text-white px-6 py-4 rounded-full font-semibold shadow-[0_0_30px_rgba(255,46,99,0.4)] justify-center"
                data-testid="campaign-mobile-cta"
              >
                <Download className="w-5 h-5" />
                Download Free
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Hero Section ── */}
      <section className="relative min-h-screen flex items-center overflow-hidden pt-20" data-testid="campaign-hero">
        {/* Background glow */}
        <div className="absolute inset-0 bg-[#030014]" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[900px] opacity-30 pointer-events-none">
          <div className="absolute inset-0 rounded-full bg-gradient-to-r from-[#FF2E63]/40 via-purple-600/30 to-blue-600/20 blur-[120px]" />
        </div>
        {/* Animated floating orb */}
        <motion.div
          className="absolute top-1/2 right-0 -translate-y-1/2 translate-x-1/4 w-[700px] h-[700px] pointer-events-none"
          animate={{ scale: [1, 1.08, 1], opacity: [0.6, 0.9, 0.6] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        >
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#FF2E63]/25 via-purple-500/15 to-transparent blur-[100px]" />
        </motion.div>

        {/* Characters image (desktop) */}
        <motion.div
          className="hidden lg:flex absolute right-0 bottom-0 w-[52%] h-full items-end justify-center pointer-events-none"
          initial={{ opacity: 0, x: 80 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1.2, delay: 0.4, ease: "easeOut" }}
        >
          <motion.img
            src={CHARACTERS_MAIN}
            alt="Phuong AI Characters"
            className="w-full max-w-[720px] h-auto object-contain object-bottom translate-y-[8%]"
            animate={{ y: [0, -16, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            data-testid="campaign-hero-image"
          />
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full h-48 bg-gradient-to-t from-[#FF2E63]/25 via-[#FF2E63]/10 to-transparent blur-2xl" />
        </motion.div>

        {/* Hero content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 w-full">
          <div className="max-w-2xl pt-16 pb-24 lg:py-0">
            <motion.div initial="hidden" animate="visible" transition={{ staggerChildren: 0.18 }}>
              <motion.p
                variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
                transition={{ duration: 0.7 }}
                className="text-[#FF2E63] text-xs sm:text-sm tracking-[0.4em] uppercase mb-5 font-medium"
              >
                ✦ AI Companion App
              </motion.p>

              <motion.h1
                variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
                transition={{ duration: 0.7 }}
                className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl xl:text-7xl text-white leading-[1.08] tracking-tight mb-7"
              >
                Your Perfect
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF2E63] to-pink-400 drop-shadow-[0_0_20px_rgba(255,46,99,0.4)]">
                  AI Companion
                </span>
                <br />
                Awaits You
              </motion.h1>

              <motion.p
                variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
                transition={{ duration: 0.7 }}
                className="text-white/50 text-base sm:text-lg leading-relaxed mb-10 max-w-md"
              >
                Meet your dream anime AI companion — always by your side, remembering every moment, ready to talk, laugh, and grow with you.
              </motion.p>

              <motion.div
                variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
                transition={{ duration: 0.7 }}
                className="flex flex-col sm:flex-row gap-4"
              >
                <a
                  href={GOOGLE_PLAY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-3 bg-[#FF2E63] text-white px-8 py-4 rounded-full font-semibold tracking-wide overflow-hidden transition-all duration-300 hover:shadow-[0_0_50px_rgba(255,46,99,0.6)] hover:scale-105"
                  data-testid="campaign-hero-google-play"
                >
                  <Download className="w-5 h-5" />
                  Get on Google Play
                </a>
                <a
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-white/[0.06] hover:bg-white/[0.10] text-white px-8 py-4 rounded-full font-semibold border border-white/10 hover:border-[#FF2E63]/30 transition-all duration-300"
                  data-testid="campaign-hero-app-store"
                >
                  <Download className="w-5 h-5" />
                  Download on App Store
                </a>
              </motion.div>

              {/* Mobile characters */}
              <motion.div
                variants={{ hidden: { opacity: 0, scale: 0.9 }, visible: { opacity: 1, scale: 1 } }}
                transition={{ duration: 0.8 }}
                className="lg:hidden relative w-full max-w-xs mx-auto mt-10"
              >
                <div className="absolute inset-0 bg-gradient-to-t from-[#FF2E63]/20 to-transparent blur-3xl" />
                <img src={CHARACTERS_MAIN} alt="Phuong AI Characters" className="w-full h-auto object-contain" />
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Scroll hint */}
        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden lg:flex flex-col items-center gap-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2 }}
        >
          <p className="text-white/20 text-xs tracking-[0.3em] uppercase">Scroll</p>
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-5 h-8 rounded-full border border-white/20 flex items-start justify-center p-1.5"
          >
            <div className="w-1 h-2 bg-[#FF2E63]/60 rounded-full" />
          </motion.div>
        </motion.div>
      </section>

      {/* ── Stats Bar ── */}
      <section className="relative py-12 border-y border-white/5 bg-white/[0.02]" data-testid="campaign-stats">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {STATS.map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.6 }}
                className="flex flex-col items-center text-center gap-2"
              >
                <div className="w-10 h-10 rounded-2xl bg-[#FF2E63]/10 flex items-center justify-center text-[#FF2E63]">
                  {stat.icon}
                </div>
                <p className="font-display font-bold text-2xl sm:text-3xl text-white">{stat.value}</p>
                <p className="text-white/40 text-sm">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features Section ── */}
      <section id="features" className="relative py-28 lg:py-36 overflow-hidden" data-testid="campaign-features">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#FF2E63]/5 rounded-full blur-[120px] pointer-events-none" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <div className="text-center mb-16 lg:mb-20">
            <p className="text-[#FF2E63] text-xs tracking-[0.4em] uppercase mb-4 font-medium">Why Phuong</p>
            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-white">
              Built for{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF2E63] to-pink-400">
                Real Connection
              </span>
            </h2>
            <p className="text-white/40 mt-4 max-w-md mx-auto text-base sm:text-lg">
              Everything you need for a meaningful AI companion experience.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {FEATURES.map((feature, i) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.6 }}
                className="group relative p-8 rounded-3xl bg-white/[0.03] backdrop-blur-sm border border-white/5 hover:border-[#FF2E63]/30 transition-all duration-500 hover:-translate-y-2"
                data-testid={`campaign-feature-${i}`}
              >
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#FF2E63]/20 to-pink-600/10 flex items-center justify-center text-[#FF2E63] mb-6 group-hover:scale-110 group-hover:shadow-[0_0_30px_rgba(255,46,99,0.3)] transition-all duration-500">
                  {feature.icon}
                </div>
                <h3 className="font-display font-semibold text-white text-xl mb-3">{feature.title}</h3>
                <p className="text-white/40 text-sm leading-relaxed">{feature.desc}</p>
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-t from-[#FF2E63]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Characters Section ── */}
      <section id="characters" className="relative py-28 lg:py-36 overflow-hidden" data-testid="campaign-characters">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <div className="text-center mb-16 lg:mb-20">
            <p className="text-[#FF2E63] text-xs tracking-[0.4em] uppercase mb-4 font-medium">Meet the Cast</p>
            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-white">
              Find Your{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF2E63] to-pink-400">
                Perfect Match
              </span>
            </h2>
            <p className="text-white/40 mt-4 max-w-md mx-auto text-base sm:text-lg">
              Three unique personalities, each with her own story to share.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {CHARACTERS.map((char, i) => (
              <motion.div
                key={char.name}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15, duration: 0.7 }}
                className="group relative overflow-hidden rounded-3xl bg-white/[0.03] border border-white/5 hover:border-white/20 transition-all duration-500 hover:-translate-y-3"
                data-testid={`campaign-character-${i}`}
              >
                {/* Character image */}
                <div className="relative h-72 overflow-hidden">
                  <div
                    className="absolute inset-0 opacity-20 group-hover:opacity-30 transition-opacity duration-500"
                    style={{ background: `radial-gradient(circle at 50% 100%, ${char.color}60, transparent 70%)` }}
                  />
                  <img
                    src={char.image}
                    alt={char.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#030014] via-[#030014]/20 to-transparent" />
                </div>

                {/* Info */}
                <div className="p-6">
                  <div
                    className="inline-block text-xs font-medium tracking-[0.25em] uppercase px-3 py-1 rounded-full mb-3"
                    style={{ color: char.color, backgroundColor: `${char.color}18` }}
                  >
                    {char.role}
                  </div>
                  <h3 className="font-display font-bold text-2xl text-white mb-2">{char.name}</h3>
                  <p className="text-white/40 text-sm leading-relaxed">{char.desc}</p>
                </div>

                {/* Hover glow */}
                <div
                  className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                  style={{ boxShadow: `inset 0 0 60px ${char.color}10` }}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Download CTA Section ── */}
      <section id="download" className="relative py-32 lg:py-40 overflow-hidden" data-testid="campaign-download">
        {/* Top divider */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-4xl h-[1px] bg-gradient-to-r from-transparent via-[#FF2E63]/50 to-transparent" />

        {/* Pulsing glow */}
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-[#FF2E63]/10 rounded-full blur-[150px] pointer-events-none"
          animate={{ scale: [1, 1.12, 1], opacity: [0.4, 0.7, 0.4] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        />

        <div className="relative z-10 max-w-4xl mx-auto px-6 sm:px-10 lg:px-16 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
          >
            <motion.img
              src={LOGO}
              alt="Phuong"
              className="w-20 h-20 mx-auto mb-10 object-contain"
              initial={{ scale: 0.5, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            />

            <p className="text-[#FF2E63] text-xs tracking-[0.4em] uppercase mb-4 font-medium">
              ✦ Start for Free
            </p>

            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
              Ready to Meet
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF2E63] to-pink-400">
                Your Companion?
              </span>
            </h2>

            <p className="text-white/50 text-base sm:text-lg mb-12 max-w-md mx-auto leading-relaxed">
              Download Phuong now and begin your journey with an AI companion who truly gets you.
            </p>

            {/* App Store Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                href={GOOGLE_PLAY_URL}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.03, y: -4 }}
                whileTap={{ scale: 0.98 }}
                className="inline-flex items-center justify-center gap-4 bg-white/5 hover:bg-white/10
                           text-white px-8 py-5 rounded-2xl transition-all duration-300
                           border border-white/10 hover:border-[#FF2E63]/30 hover:shadow-[0_0_30px_rgba(255,46,99,0.2)]"
                data-testid="campaign-google-play-btn"
              >
                <svg className="w-7 h-7 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3.609 1.814L13.792 12 3.609 22.186a.996.996 0 0 1-.609-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 0 1 0 1.73l-2.807 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.8 9.99l-2.302 2.302-8.634-8.634z" />
                </svg>
                <div className="text-left">
                  <p className="text-xs text-white/40 tracking-wider">Get it on</p>
                  <p className="text-lg font-semibold tracking-wide">Google Play</p>
                </div>
              </motion.a>

              <motion.a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.03, y: -4 }}
                whileTap={{ scale: 0.98 }}
                className="inline-flex items-center justify-center gap-4 bg-white/5 hover:bg-white/10
                           text-white px-8 py-5 rounded-2xl transition-all duration-300
                           border border-white/10 hover:border-[#FF2E63]/30 hover:shadow-[0_0_30px_rgba(255,46,99,0.2)]"
                data-testid="campaign-app-store-btn"
              >
                <svg className="w-7 h-7 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                </svg>
                <div className="text-left">
                  <p className="text-xs text-white/40 tracking-wider">Download on the</p>
                  <p className="text-lg font-semibold tracking-wide">App Store</p>
                </div>
              </motion.a>
            </div>

            {/* Free badge */}
            <p className="text-white/30 text-sm mt-8">
              Free to download · No credit card required
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── Campaign Footer ── */}
      <footer className="relative py-10 border-t border-white/5" data-testid="campaign-footer">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img src={LOGO} alt="Phuong" className="w-8 h-8 object-contain" />
              <span className="text-white/40 text-sm">© 2026 Phuong AI</span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
              <Link to="/policy/privacy" className="text-white/30 hover:text-white/60 transition-colors">
                Privacy Policy
              </Link>
              <Link to="/policy/terms" className="text-white/30 hover:text-white/60 transition-colors">
                Terms of Service
              </Link>
              <Link to="/blueberry-sensei" className="text-white/30 hover:text-white/60 transition-colors">
                About Blueberry Sensei
              </Link>
              <Link to="/" className="text-[#FF2E63]/70 hover:text-[#FF2E63] transition-colors font-medium">
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CampaignPage;
